import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { nanoid } from 'nanoid';
import { cloneDeep } from 'lodash';

// Actions
import type {
  OrderErrors,
  PartnerBonuses,
  PostOrdersPrebuildSuccessResponse,
} from '@lt/api/dist/methods/orders/types';
import { toggleFlightClassAction } from 'checkout/CheckoutRoutes/PackageCheckout/store/flights/slice';
import {
  confirmClientAction,
  resetClientAction,
} from 'src/store/client/actions';
import { changeTouristData } from 'src/store/tourists/slice';
import type { Force } from 'src/types/utility';
import { clearPreviousPackageDataAction } from '../actions/package';
import { resetTouristConfirmedAction } from '../actions/tourists';
import { validationSuccessAction } from '../validation/actions';

import { ORDER_COMMENT_CHECKBOXES } from '../constants/order';

import type {
  CreateOrderSuccessPayload,
  OrderComment,
  OrderStateType,
  MosgortourSnils,
  PrebuildOrderPayload,
  SetFlightInfoPayload,
  SetRedirectUrlPayload,
  UpdateFlightInfoPayload,
  UpdateOrderSuccessPayload,
  DeleteMosgortourCertificatePayload,
  UpdateMosgortourCertificatePayload,
  CheckMosgortourCertificateTouristPayload,
  UpdateMosgortourSnilsPayload,
  SetMosgortourPrebuildDataPayload,
} from '../order/types';

const createMosgortourCertificate = () => ({
  id: nanoid(),
  number: '',
  isValid: false,
  isFetching: false,
  error: null,
  checkedTourists: [],
});

const initialState: OrderStateType = {
  isCreating: false,
  jointOrder: {
    ordersAmount: 0,
    firstOrderNumber: 0,
    isFirstOrder: null,
  },
  isProcessed: false,
  isPrebuilt: false,
  insurancePrice: 0,
  orderExtras: [],
  discountMethods: [],
  discountPrice: 0,
  discountType: null,
  fullPrice: 0,
  isClientConfirmed: false,
  mosgortour: {
    snilses: [],
    prebuild: [],
    certificates: [createMosgortourCertificate()],
  },
  bonuses: {
    cashback_identifier: null,
    bonuses_absolut: null,
    bonus_total: 0,
    fuelfree_bonuses_relative: 0,
    max_value: null,
    promotion_fixed: null,
    promotion_value: 0,
  },
  isTouristsConfirmed: false,
  showOrderErrorPopup: false,
  priceInfo: [],
  isFinished: false,
  isPrebuildFetching: false,
  isProcessing: false,
  isCreated: false,
  comment: {
    text: '',
    checkboxes: ORDER_COMMENT_CHECKBOXES,
  },
  redirectUrl: null,
  partnerBonuses: null,
};

const updateFlightInfo = (
  state: OrderStateType,
  payload: UpdateFlightInfoPayload,
) => {
  const { flight_pair_id, extras, originalFlight } = payload;
  return {
    ...state,
    flight_pair_id,
    flight_extras: extras,
    originalFlight,
  };
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    // Flights
    selectFlight: (state, action: PayloadAction<UpdateFlightInfoPayload>) =>
      updateFlightInfo(state, action.payload),
    deselectFlight: (state) => {
      state.flight_pair_id = undefined;
      state.flight_extras = null;
      state.flightInfo = null;
    },
    skipFlights: (state) => ({
      ...state,
      isSkipFlights: true,
    }),
    setFlightInfo: (state, action: PayloadAction<SetFlightInfoPayload>) => ({
      ...state,
      flightInfo: action.payload,
    }),
    // Order Comment
    changeOrderComment: (state, action: PayloadAction<OrderComment>) => ({
      ...state,
      comment: action.payload,
    }),
    /**
     * Добавление в стор общего числа заказов,
     * если выбраны совместные заказы
     */
    setOrdersAmount: (state, action: PayloadAction<number>) => ({
      ...state,
      jointOrder: {
        ...state.jointOrder,
        ordersAmount: action.payload,
      },
    }),
    /**
     * Добавление в стор номера первого заказа,
     * если выбраны совместные заказы
     */
    setFirstOrderNumber: (state, action: PayloadAction<number>) => ({
      ...state,
      jointOrder: {
        ...state.jointOrder,
        firstOrderNumber: action.payload,
      },
    }),
    /**
     * Изменение заказа с дополнительного на первый
     * либо наоборот, если выбраны совместные заказаы
     */
    switchOrder: (state) => ({
      ...state,
      jointOrder: {
        ...state.jointOrder,
        isFirstOrder: !state.jointOrder.isFirstOrder,
      },
    }),
    /**
     * Если выбраны совместные заказы по умолчанию грузится первый заказ
     */
    setFirstOrderField: (state, action: PayloadAction<boolean | null>) => ({
      ...state,
      jointOrder: {
        ...state.jointOrder,
        isFirstOrder: action.payload,
      },
    }),
    // Preuild Order
    prebuildOrder: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _: PayloadAction<PrebuildOrderPayload | undefined>,
    ) => ({
      ...state,
      isPrebuildFetching: true,
    }),
    prebuildOrderFail: (state, action: PayloadAction<OrderErrors>) => ({
      ...state,
      isPrebuildFetching: false,
      errors: action.payload,
    }),
    prebuildOrderSuccess: (
      state,
      action: PayloadAction<PostOrdersPrebuildSuccessResponse>,
    ) => {
      const {
        priceInfo,
        fullPrice,
        bonuses,
        coupon,
        discountMethods,
        discountPrice,
        discountType,
        insurancePrice,
        orderExtras,
        bonusNotice,
      } = action.payload;

      return {
        ...state,
        priceInfo,
        fullPrice,
        bonuses,
        coupon,
        discountMethods,
        discountPrice,
        discountType,
        orderExtras,
        insurancePrice,
        isPrebuildFetching: false,
        isPrebuilt: true,
        errors: null,
        bonusNotice,
      };
    },
    prebuildOrderCompleteWithMindboxErrors: (
      state,
      action: PayloadAction<
        Force<PostOrdersPrebuildSuccessResponse, 'orderErrors'>
      >,
    ) => {
      const {
        priceInfo,
        fullPrice,
        bonuses,
        coupon,
        discountMethods,
        discountPrice,
        discountType,
        insurancePrice,
        orderErrors,
        orderExtras,
      } = action.payload;

      return {
        ...state,
        priceInfo,
        fullPrice,
        bonuses,
        coupon,
        discountMethods,
        discountPrice,
        discountType,
        orderExtras,
        insurancePrice,
        isPrebuildFetching: false,
        isPrebuilt: true,
        errors: orderErrors,
      };
    },
    // Create order
    createOrder: (state) => ({
      ...state,
      isCreating: true,
    }),
    createOrderSuccess: (
      state,
      action: PayloadAction<CreateOrderSuccessPayload>,
    ) => {
      const { id, pin } = action.payload;

      return {
        ...state,
        id,
        pin,
        isCreated: true,
        isCreating: false,
      };
    },
    createOrderFail: (state) => ({
      ...state,
      isCreating: false,
    }),
    // Update order
    updateOrder: (
      state,
      action: PayloadAction<Partial<OrderStateType> | undefined>,
    ) => ({
      ...state,
      ...action.payload,
    }),
    updateOrderSuccess: (
      state,
      action: PayloadAction<UpdateOrderSuccessPayload>,
    ) => {
      const { price_info, full_price, payment_methods } = action.payload;

      return {
        ...state,
        priceInfo: price_info,
        fullPrice: full_price,
        payment_methods,
      };
    },

    // Sub checks
    stashUserPriceBeforePay: (
      state,
      action: PayloadAction<number | undefined>,
    ) => ({
      ...state,
      priceOnPay: action.payload,
    }),
    // Order process
    orderProcessed: (state) => ({
      ...state,
      isProcessed: true,
      isProcessing: false,
    }),
    confirmOrder: (state) => ({
      ...state,
      priceOnPay: null,
    }),
    finishOrder: (state) => ({
      ...state,
      isFinished: true,
    }),

    // Mosgortour certificates
    addMosgortourCertificate: (state) => ({
      ...state,
      mosgortour: {
        ...state.mosgortour,
        certificates: [
          ...state.mosgortour.certificates,
          {
            ...createMosgortourCertificate(),
            number: '',
            isValidationReady: false,
          },
        ],
      },
    }),
    deleteMosgortourCertificate: (
      state,
      action: PayloadAction<DeleteMosgortourCertificatePayload>,
    ) => ({
      ...state,
      mosgortour: {
        ...state.mosgortour,
        certificates: state.mosgortour.certificates.filter(
          (certificate) => certificate.id !== action.payload.id,
        ),
      },
    }),
    updateMosgortourCertificate: (
      state,
      action: PayloadAction<UpdateMosgortourCertificatePayload>,
    ) => {
      const index = state.mosgortour.certificates.findIndex(
        (element) => element.id === action.payload.id,
      );

      if (index === -1) return state;

      const certificate = {
        ...state.mosgortour.certificates[index],
        ...action.payload,
      };

      return {
        ...state,
        mosgortour: {
          ...state.mosgortour,
          certificates: state.mosgortour.certificates.with(index, certificate),
        },
      };
    },
    checkMosgortourCertificateTourist: (
      state,
      action: PayloadAction<CheckMosgortourCertificateTouristPayload>,
    ) => {
      const index = state.mosgortour.certificates.findIndex(
        (element) => element.id === action.payload.id,
      );

      if (index === -1) return state;

      const certificate = cloneDeep(state.mosgortour.certificates[index]);

      if (certificate.checkedTourists.includes(action.payload.index))
        return state;

      certificate.checkedTourists.push(action.payload.index);

      return {
        ...state,
        mosgortour: {
          ...state.mosgortour,
          certificates: state.mosgortour.certificates.with(index, certificate),
        },
      };
    },
    uncheckMosgortourCertificateTourist: (
      state,
      action: PayloadAction<CheckMosgortourCertificateTouristPayload>,
    ) => {
      const index = state.mosgortour.certificates.findIndex(
        (element) => element.id === action.payload.id,
      );

      if (index === -1) return state;

      const certificate = cloneDeep(state.mosgortour.certificates[index]);
      certificate.checkedTourists = certificate.checkedTourists.filter(
        (element) => element !== action.payload.index,
      );

      return {
        ...state,
        mosgortour: {
          ...state.mosgortour,
          certificates: state.mosgortour.certificates.with(index, certificate),
        },
      };
    },
    updateMosgortourSnils: (
      state,
      action: PayloadAction<UpdateMosgortourSnilsPayload>,
    ) => {
      const index = state.mosgortour.snilses.findIndex(
        (element) => element.touristIndex === action.payload.touristIndex,
      );

      if (index === -1) {
        const snils: MosgortourSnils = {
          snilsNumber: '',
          isValid: false,
          ...action.payload,
        };

        return {
          ...state,
          mosgortour: {
            ...state.mosgortour,
            snilses: state.mosgortour.snilses.concat(snils),
          },
        };
      }

      const snils = { ...state.mosgortour.snilses[index], ...action.payload };

      return {
        ...state,
        mosgortour: {
          ...state.mosgortour,
          snilses: state.mosgortour.snilses.with(index, snils),
        },
      };
    },
    setMosgortourPrebuildData: (
      state,
      action: PayloadAction<SetMosgortourPrebuildDataPayload>,
    ) => ({
      ...state,
      mosgortour: { ...state.mosgortour, prebuild: action.payload },
    }),
    // Redirect url after payment
    setRedirectUrl: (state, action: PayloadAction<SetRedirectUrlPayload>) => ({
      ...state,
      redirectUrl: action.payload.redirectUrl,
    }),
    setPartnerBonuses: (
      state,
      action: PayloadAction<PartnerBonuses | null>,
    ) => {
      state.partnerBonuses = action.payload;
    },
    clearOrderFieldError: (state, action: PayloadAction<string>) => {
      if (state.errors) {
        delete state.errors[action.payload];
        if (Object.keys(state.errors).length === 0) {
          state.errors = null;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Flights
      .addCase(toggleFlightClassAction, (state, action) =>
        updateFlightInfo(state, action.payload),
      )
      // Client
      .addCase(confirmClientAction, (state) => ({
        ...state,
        isClientConfirmed: true,
      }))
      .addCase(resetClientAction, (state) => ({
        ...state,
        isClientConfirmed: false,
      }))
      // Tourists
      .addCase(validationSuccessAction, (state) => ({
        ...state,
        isTouristsConfirmed: true,
      }))
      .addCase(changeTouristData, (state) => {
        const errors = cloneDeep(state.errors);

        if (errors) {
          delete errors.mosgortur_certificates;
        }

        return {
          ...state,
          isTouristsConfirmed: false,
          errors: errors && Object.keys(errors).length ? errors : null,
          mosgortour: {
            ...state.mosgortour,
            prebuild: [],
            certificates: [createMosgortourCertificate()],
          },
        };
      })
      .addCase(resetTouristConfirmedAction, (state) => ({
        ...state,
        isTouristsConfirmed: false,
      }))
      .addCase(clearPreviousPackageDataAction, (state) => ({
        ...state,
        flight_pair_id: undefined,
        flight_extras: null,
        flightInfo: null,
        insuranceType: null,
        coupon: null,
        discount_price: null,
        discount_type: null,
        errors: null,
        priceInfo: null,
        comment: {
          text: '',
          checkboxes: ORDER_COMMENT_CHECKBOXES,
        },
        jointOrder: {
          ordersAmount: 0,
          firstOrderNumber: 0,
          isFirstOrder: null,
        },
      }));
  },
});

export const {
  selectFlight,
  skipFlights,
  setFlightInfo,
  changeOrderComment,
  setOrdersAmount,
  setFirstOrderNumber,
  switchOrder,
  setFirstOrderField,
  prebuildOrder,
  prebuildOrderFail,
  prebuildOrderSuccess,
  prebuildOrderCompleteWithMindboxErrors,
  createOrder,
  createOrderSuccess,
  createOrderFail,
  updateOrder,
  updateOrderSuccess,
  stashUserPriceBeforePay,
  orderProcessed,
  confirmOrder,
  finishOrder,
  deselectFlight,
  addMosgortourCertificate,
  deleteMosgortourCertificate,
  updateMosgortourCertificate,
  checkMosgortourCertificateTourist,
  uncheckMosgortourCertificateTourist,
  updateMosgortourSnils,
  setMosgortourPrebuildData,
  setRedirectUrl,
  setPartnerBonuses,
  clearOrderFieldError,
} = orderSlice.actions;

export default orderSlice.reducer;
